import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CheckCircle2Icon } from "lucide-react";
import { useHackathon, type NewMentor, type NewStudent } from "../../contexts/HackathonContext";
import { PublicLayout } from "../../components/layout/PublicLayout";
import { StudentFields } from "../../components/profile/StudentFields";
import { MentorFields } from "../../components/profile/MentorFields";
export function JoinProfile() {
  const {
    token
  } = useParams();
  const {
    state,
    updateStudent,
    updateMentor
  } = useHackathon();
  const student = state.students.find(s => s.inviteToken === token);
  const mentor = state.mentors.find(m => m.inviteToken === token);
  const [studentDraft, setStudentDraft] = useState<NewStudent | null>(null);
  const [mentorDraft, setMentorDraft] = useState<NewMentor | null>(null);
  const [done, setDone] = useState(false);
  useEffect(() => {
    if (student && !studentDraft) {
      const {
        id: _i,
        inviteToken: _t,
        joinedAt: _j,
        ...rest
      } = student;
      setStudentDraft(rest);
    }
    if (mentor && !mentorDraft) {
      const {
        id: _i,
        inviteToken: _t,
        joinedAt: _j,
        ...rest
      } = mentor;
      setMentorDraft(rest);
    }
  }, [student, mentor, studentDraft, mentorDraft]);
  if (!student && !mentor) {
    return <PublicLayout data-id="element-1078" data-mp="b0qevlraidmc">
        <h1 className="text-2xl font-semibold" data-id="element-1079" data-mp="b18fsvowdg0l">This invite link isn't valid</h1>
        <p className="mt-2 text-muted" data-id="element-1080" data-mp="18nywv6s314jl">Please ask the program team for a new link.</p>
      </PublicLayout>;
  }
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const joinedAt = new Date().toISOString();
    if (student && studentDraft) updateStudent(student.id, {
      ...studentDraft,
      name: studentDraft.name.trim(),
      joinedAt
    });
    if (mentor && mentorDraft) updateMentor(mentor.id, {
      ...mentorDraft,
      name: mentorDraft.name.trim(),
      joinedAt
    });
    setDone(true);
    window.scrollTo({
      top: 0
    });
  };
  const firstName = (student ?? mentor)!.name.split(" ")[0];
  if (done) {
    return <PublicLayout data-id="element-1081" data-mp="3gwlvcvb5jtlw">
        <CheckCircle2Icon className="h-10 w-10 text-brgreen" aria-hidden="true" data-id="element-1082" data-mp="3gxf2hxe0wl4k" />
        <h1 className="mt-4 text-3xl font-semibold tracking-tight" data-id="element-1083" data-mp="3kwe5vdk3w9tw">You're all set, {firstName}</h1>
        <p className="mt-2 text-[15px] text-muted" data-id="element-1084" data-mp="374zk4yytduj5">
          Your profile is saved. You can come back to this link any time to update it.
        </p>
        <div className="mt-8 flex flex-wrap gap-3" data-id="element-1085" data-mp="2x7l3ozcw55qu">
          {student && <Link to={`/checkin/${student.inviteToken}`} className="rounded-xl bg-ink px-5 py-3 text-sm font-semibold text-white transition-colors duration-150 hover:bg-ink/90" data-id="element-1086" data-mp="2x9o3uc7rcf9a">
              Go to today's video check-in
            </Link>}
          <button type="button" onClick={() => setDone(false)} className="rounded-xl border border-ink/15 bg-surface px-5 py-3 text-sm font-semibold transition-colors duration-150 hover:bg-paper" data-id="element-1087" data-mp="2t7krlld9l6my">
            Edit my profile
          </button>
        </div>
      </PublicLayout>;
  }
  return <PublicLayout data-id="element-1088" data-mp="wk03qhub2upx">
      <p className="text-sm font-semibold text-saffron-ink" data-id="element-1089" data-mp="wki3vpk39t34">{student ? `Student · Group ${student.groupId || "TBD"}` : "Mentor"}</p>
      <h1 className="mt-1 text-3xl font-semibold tracking-tight" data-id="element-1090" data-mp="1acg93paoj4u0">Welcome, {firstName}</h1>
      <p className="mt-2 text-[15px] text-muted" data-id="element-1091" data-mp="15hsi9js7dyc9">
        Tell us a bit about yourself. Your profile appears in the program directory for teammates, mentors and investors.
      </p>

      <form onSubmit={submit} className="mt-8 rounded-2xl border border-line bg-surface p-6" data-id="element-1092" data-mp="1f96mu5z0hoc9">
        {studentDraft && <StudentFields value={studentDraft} onChange={setStudentDraft} mode="self" data-id="element-1093" data-mp="1t8oequ7lthpu" />}
        {mentorDraft && <MentorFields value={mentorDraft} onChange={setMentorDraft} mode="self" data-id="element-1094" data-mp="1p5ru6xyi7bj9" />}
        <div className="mt-8 flex justify-end" data-id="element-1095" data-mp="3v1cmngv70tby">
          <button type="submit" className="rounded-xl bg-ink px-6 py-3 text-sm font-semibold text-white transition-[transform,background-color] duration-150 hover:bg-ink/90 active:scale-[0.97]" data-id="element-1096" data-mp="3qzirfo3rohj6">
            Save my profile
          </button>
        </div>
      </form>
    </PublicLayout>;
}